import { FC, useCallback, useRef, useState } from 'react';
import { RichTextEditor, type RichTextEditorHandle } from '@/shared/ui';
import { buildKnowledgeNodeMarker } from './commentMarkers';
import { KnowledgeNodeSuggestions } from './KnowledgeNodeSuggestions';

type Props = {
  /** Набранный текст с разметкой */
  value: string;
  onChange: (html: string) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  readOnly?: boolean;
};

/** Незавершённое обращение к узлам знаний: знак `@` и набранное после него до курсора. */
const KNOWLEDGE_NODE_QUERY = /@(\S*)$/;

/**
 * Поле ввода комментария правила: знак `@` открывает список узлов знаний, выбранный узел встаёт
 * в текст ссылкой.
 */
export const EvaluationRuleCommentField: FC<Props> = ({ value, onChange, onKeyDown, readOnly }) => {
  const [suggestionQuery, setSuggestionQuery] = useState<string | null>(null);
  const editorRef = useRef<RichTextEditorHandle>(null);
  /** Позиция `@запроса` на момент набора: к клику по подсказке редактор уже потерял выделение */
  const mentionRangeRef = useRef<{ index: number; length: number } | null>(null);

  const handleChange = useCallback(
    (html: string) => {
      onChange(html);

      // Позицию курсора читаем после того, как редактор применит правку
      requestAnimationFrame(() => {
        const editor = editorRef.current;
        const cursorIndex = editor?.getSelection()?.index;
        if (!editor || cursorIndex == null) return;

        const queryMatch = editor.getText(0, cursorIndex).match(KNOWLEDGE_NODE_QUERY);
        mentionRangeRef.current = queryMatch
          ? { index: cursorIndex - queryMatch[0].length, length: queryMatch[0].length }
          : null;
        setSuggestionQuery(queryMatch ? queryMatch[1] : null);
      });
    },
    [onChange],
  );

  const applyKnowledgeNode = useCallback((nodeId: number) => {
    const mention = mentionRangeRef.current;
    if (!mention) return;

    // Ввод продолжается сразу за вставленной ссылкой
    editorRef.current?.replaceText(mention.index, mention.length, buildKnowledgeNodeMarker(nodeId));
    editorRef.current?.focus();
    mentionRangeRef.current = null;
    setSuggestionQuery(null);
  }, []);

  return (
    <div className="relative flex-1">
      <RichTextEditor
        ref={editorRef}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        readOnly={readOnly}
        autoFocus
        // Ограничиваем высоту поля: без этого длинный комментарий растягивает popover за экран
        editorClassName="[&_.ql-editor]:max-h-48 [&_.ql-editor]:min-h-20"
      />
      {suggestionQuery !== null && <KnowledgeNodeSuggestions query={suggestionQuery} onSelect={applyKnowledgeNode} />}
    </div>
  );
};
